import { Endpoint } from '.'
import {
    AuthenticationError,
    BadRequestError,
    HttpRequestError,
} from '../models/ApplicationErrors'
import { GENERAL_ERROR } from '../utils'
import { getAccessToken, handleAuthenticationError } from './auth'
import { transformData } from './converter'
import {
    Headers,
    Methods,
    ContentTypes,
    ResponseStatuses,
    getApiUrl,
    getBearerTokenAuthenticationHeader,
} from './utils'

const defaultHeaders = {
    [Headers.Accept]: ContentTypes.ApplicationJson,
    [Headers.ContentType]: ContentTypes.ApplicationJson,
}

const formDataHeaders = {
    [Headers.Accept]: ContentTypes.ApplicationJson,
}

interface RequestConfig {
    endpoint: Endpoint
    headers?: { [key: string]: string }
    body?: object | FormData
    queryParams?: { [key: string]: string | boolean }
    pathParams?: { [key: string]: string }
}

export interface HttpResponse<T> {
    data?: T
    errors?: string[]
}

// throwOnError should be set to true if using this in react query hook
export const executeHttp = async <T>(
    config: RequestConfig,
    throwOnError = false
): Promise<HttpResponse<T>> => {
    let promise: Promise<T>
    switch (config.endpoint.method) {
        case Methods.Get:
            promise = httpGet(config)
            break
        case Methods.Post:
            promise = httpPost(config)
            break
        case Methods.Put:
            promise = httpPut(config)
            break
        case Methods.Patch:
            promise = httpPatch(config)
            break
        case Methods.Delete:
            promise = httpDelete(config)
            break
        default:
            throw new Error(
                `Method ${config.endpoint.method} is not supported!`
            )
    }
    try {
        const data = await promise
        return { data }
    } catch (e) {
        let errors = [GENERAL_ERROR]
        if (e instanceof AuthenticationError)
            handleAuthenticationError(
                location.pathname.startsWith('/portal')
                    ? 'DevPortal'
                    : 'Internal'
            )
        if (e instanceof BadRequestError)
            errors = e.errors
                ? e.errors.map((error) => error.messages).flat()
                : errors
        // react query requires that we throw an error in order to work properly
        if (throwOnError) throw { errors }
        return { errors }
    }
}

const httpGet = async <T>(config: RequestConfig): Promise<T> => {
    return httpRequest(Methods.Get, config)
}

const httpPost = async <T>(config: RequestConfig): Promise<T> => {
    return httpRequest(Methods.Post, config)
}

const httpPut = async <T>(config: RequestConfig): Promise<T> => {
    return httpRequest(Methods.Put, config)
}

const httpDelete = async <T>(config: RequestConfig): Promise<T> => {
    return httpRequest(Methods.Delete, config)
}

const httpPatch = async <T>(config: RequestConfig): Promise<T> => {
    return httpRequest(Methods.Patch, config)
}

export const httpRequest = async <T>(
    method: string,
    { headers, endpoint, body, queryParams, pathParams }: RequestConfig
): Promise<T> => {
    const isFormData = body instanceof FormData

    let requestHeaders = {
        ...(isFormData ? formDataHeaders : defaultHeaders),
        ...headers,
    }

    if (endpoint.authenticationType) {
        if (!requestHeaders[Headers.Authorization]) {
            const accessToken = getAccessToken(endpoint.authenticationType)
            if (accessToken && accessToken.value) {
                requestHeaders = {
                    ...requestHeaders,
                    ...getBearerTokenAuthenticationHeader(accessToken.value),
                }
            }
        }
    }

    const args = {
        method: method,
        body: isFormData ? body : JSON.stringify(body),
        headers: requestHeaders,
    }

    const url = getApiUrl(endpoint, pathParams, queryParams)
    const request = new Request(url.toString(), args)
    const response = await fetch(request)
    if (!response.ok) {
        switch (response.status) {
            case ResponseStatuses.Unauthorized:
                throw new AuthenticationError()
            case ResponseStatuses.BadRequest:
                // eslint-disable-next-line no-case-declarations
                const errorResponse = await response.json()
                // eslint-disable-next-line no-case-declarations
                const errors = errorResponse['errors']
                throw new BadRequestError(errors)
        }
        throw new HttpRequestError(response.status)
    }
    const data = await response.json()
    return transformData(data)
}
